import { useEffect , useState} from 'react';
import { Modal, Form, Input, Row, Col, Typography, DatePicker, Select, message } from 'antd';
import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { LogoOnExam } from 'utils/assets/images';
import { useCurrentUser } from 'store/users/selectors';
import { handleCreateTeacherSupportForm, handleUpdateTeacherSupportForm } from 'store/logs/thunks';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const StyledModal = styled(Modal)`
  .ant-modal-wrap {
    z-index: 9999 !important;
  }
  
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }
  
  .ant-modal-mask {
    z-index: 9998 !important;
  }
  
  .ant-modal-close {
    display: none !important;
  }
  
  .ant-modal-header {
    border-bottom: none;
    padding: 0px 10px;
    position: relative;
  }
  
  .ant-modal-footer {
    border-top: 1px solid #f0f0f0;
    padding: 16px 24px;
    text-align: right;
  }
  
  .ant-btn {
    border-radius: 6px;
    font-weight: 500;
    padding: 6px 16px;
    height: auto;
  }
  
  .ant-btn-primary {
    background: #1890ff;
    border-color: #1890ff;
  }
  
  .ant-btn-primary:hover {
    background: #40a9ff;
    border-color: #40a9ff;
  }
  
  .ant-btn-default {
    border-color: #d9d9d9;
    color: #666;
  }
  
  .ant-btn-default:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FormTitle = styled(Title)`
  margin: 0 !important;
  color: #000;
  font-weight: 700;
  font-size: 33.6px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const FormContainer = styled.div`
  padding: 24px;
  background: #fff;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    background: #386b9c;
    color: #fff;
    padding: 8px 12px;
    font-weight: 600;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    min-width: 120px;
    min-height: 40px;
    text-align: left;
    width: 100%;
  }
  
  .ant-form-item-label > label.ant-form-item-required::before {
    display: none !important;
  }
 .ant-form-item .ant-form-item-label >label::after{
    display: none !important;
 } 
  
  .ant-form-item-control-input {
    flex: 1;
  }
`;

const FormLabel = styled.span`
  color: #fff;
  padding: 7px;
  align-items: center;
  font-weight: 600;
  display: block;
  width: 100%;
`;

const StyledInput = styled(Input)`
  border: 1px solid #d9d9d9;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 14px;
  transition: all 0.3s;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const StyledTextArea = styled(TextArea)`
  border: 1px solid #d9d9d9;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 14px;
  transition: all 0.3s;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  .ant-picker-input > input {
    font-size: 14px;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 40px;
  .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
    padding: 8px 12px !important;
    font-size: 14px !important;
    transition: all 0.3s !important;
  }
  
  &:hover .ant-select-selector {
    border-color: #40a9ff !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #40a9ff !important;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
  }
`;


const CloseButton = styled(IconButton)`
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    z-index: 10;
    
    &:hover {
        background: rgba(255, 255, 255, 1);
        transform: scale(1.1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
`;

const TeacherSupportForm = ({ visible, onClose, userData, editData, onUpdateSuccess }) => {
  const [form] = Form.useForm();
  const currentUser = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (visible) {
      setIsDirty(false);
      if (editData) {
        form.setFieldsValue({
          district: editData?.district || '',
          teacher: editData?.teacher || '',
          dateTime: editData?.date_time ? dayjs(editData.date_time) : dayjs(),
          duration: editData?.duration_minutes || '',
          reason: editData?.reason_for_meeting || '',
          solution: editData?.solution || '',
          supportMember: editData?.support_member || '',
          meetingLink: editData?.meeting_link || '',
          jiraURL: editData?.jira_url || '',
          status: editData?.status || '',
          note: editData?.note || ''
        });
      } else {
        form.setFieldsValue({
          dateTime: dayjs(),
          district: userData?.district || '',
          teacher: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : ''
        });
      }
    } else {
      setIsDirty(false);
    }
  }, [visible, form, userData, editData]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
    if (editData && editData.id) {
      const updateData = {
        status: values.status,
        note: values.note || null,
        district: values.district,
        teacher: values.teacher,
        dateTime: values.dateTime?.format('YYYY-MM-DD HH:mm:ss'),
        duration: values.duration,
        reason: values.reason,
        solution: values.solution,
        supportMember: values.supportMember,
        meetingLink: values.meetingLink,
        jiraURL: values.jiraURL,
      };

      const result = await handleUpdateTeacherSupportForm(editData.id, updateData);
      message.success('Teacher support form updated successfully!');
      if (onUpdateSuccess) {
        let responseData = null;
        if (result?.data?.data) {
          responseData = result.data.data;
        } else if (result?.data) {
          responseData = result.data;
        } else if (result) {
          responseData = result;
        }
        
        if (responseData) {
          onUpdateSuccess(responseData);
        }
      }
    } else {
      const formData = {
        district: values.district,
        teacher: values.teacher,
        dateTime: values.dateTime?.format('YYYY-MM-DD HH:mm:ss'),
        duration: values.duration,
        reason: values.reason,
        solution: values.solution,
        supportMember: values.supportMember,
        meetingLink: values.meetingLink,
        jiraURL: values.jiraURL,
        status: values.status,
        teacherId: userData?.id,
        districtId: userData?.orgId,
        note: values.note,
        createdBy: currentUser?.id,
      };

      await handleCreateTeacherSupportForm(formData);
      message.success('Teacher support form submitted successfully!');
    }
      
      setIsDirty(false);
      onClose();
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) {
        // message.error('Please fill all required fields');
      } else {
        const errorMessage = editData ? 'Failed to update form. Please try again.' : 'Failed to submit form. Please try again.';
        message.error(errorMessage);
        console.error('Error submitting form:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setIsDirty(false);
  };

  return (
    <StyledModal
      title={
        <ModalHeader>
          <FormTitle level={3}>User Support Form</FormTitle>
          <LogoContainer>
            <LogoImage alt="logo" src={LogoOnExam} />
          </LogoContainer>
        </ModalHeader>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={1100}
      okText={editData ? "Update" : "Submit"}
      cancelText="Cancel"
      zIndex={9999}
      centered
      confirmLoading={loading}
      okButtonProps={{
        disabled: editData && !isDirty
      }}
    >
      <CloseButton onClick={handleCancel}>
        <Close color="action" />
      </CloseButton>
      <FormContainer>
        <StyledForm
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          onValuesChange={() => setIsDirty(true)}
        >
          <Row gutter={16}>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>District</FormLabel>}
                name="district"
                rules={[{ required: true, message: 'Please enter district' }]}
                labelAlign="left"
              >
                <StyledInput placeholder="Type here" readOnly/>
              </Form.Item>
            </Col>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>Date/Time</FormLabel>}
                name="dateTime"
                rules={[{ required: true, message: 'Please select date and time' }]}
                labelAlign="left"
              >
                <StyledDatePicker
                  showTime={{
                    use12Hours: true,
                    format: 'h:mm A',
                  }}
                  format="MM-DD-YYYY h:mm A"
                  placeholder="Select date and time"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>Name</FormLabel>}
                name="teacher"
                rules={[{ required: true, message: 'Please enter name' }]}
                labelAlign="left"
              >
                <StyledInput placeholder="Type here" readOnly/>
              </Form.Item>
            </Col>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>Duration</FormLabel>}
                name="duration"
                labelAlign="left"
              >
                <StyledInput type="number" placeholder="Enter duration in minutes" />
              </Form.Item>
            </Col>
            <Col sm={24} md={24} lg={24}>
              <Form.Item
                label={<FormLabel>Reason for meeting</FormLabel>}
                name="reason"
                labelCol={{ span: 4 }} 
                labelAlign="left"
                wrapperCol={{ span: 20 }}
                rules={[{ required: true, message: 'Reason for meeting information is required' }]}
              >
                <StyledTextArea rows={4} placeholder="Type here" />
              </Form.Item>
            </Col>
            <Col sm={24} md={24} lg={24}>
              <Form.Item
                label={<FormLabel>Solution</FormLabel>}
                name="solution"
                labelCol={{ span: 4 }} 
                labelAlign="left"
                wrapperCol={{ span: 20 }}
              >
                <StyledTextArea rows={4} placeholder="Type here" />
              </Form.Item>
            </Col>
            <Col sm={24} md={24} lg={24}>
              <Form.Item
                label={<FormLabel>Support Member</FormLabel>}
                name="supportMember"
                labelCol={{ span: 4 }} 
                labelAlign="left"
                wrapperCol={{ span: 20 }}
                rules={[{ required: true, message: "Support member information is required"}]}
              >
                <StyledInput placeholder="Enter support member name" />
              </Form.Item>
            </Col>
            <Col sm={24} md={24} lg={24}>
              <Form.Item
                label={<FormLabel>Meeting Link</FormLabel>}
                name="meetingLink"
                labelCol={{ span: 4 }} 
                labelAlign="left"
                wrapperCol={{ span: 20 }}
              >
                <StyledInput placeholder="Enter meeting URL" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>Jira URL</FormLabel>}
                name="jiraURL"
                labelAlign="left"
              >
                <StyledInput placeholder="Enter Jira ticket URL" />
              </Form.Item>
            </Col>
            <Col sm={12} md={12} lg={12}>
              <Form.Item
                label={<FormLabel>Status</FormLabel>}
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
                labelAlign="left"
              >
                <StyledSelect placeholder="Select status">
                  <Option value="not-started">Not Started</Option>
                  <Option value="in-progress">In Progress</Option>
                  <Option value="under-review">Under Review</Option>
                  <Option value="completed">Completed</Option>
                </StyledSelect>
              </Form.Item>
            </Col>
          </Row>
        </StyledForm>
      </FormContainer>
    </StyledModal>
  );
};

export default TeacherSupportForm;
